/**
 * A compact strip of the tracker's own achievements, shown on the tracker
 * home so progress is visible where you log (not just on /skills/level).
 * Ties the tracker into the existing gamification system — same badges, same
 * unlock modal. Earned badges are accent-lit; locked ones are dimmed goals.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getGamification } from "@/features/gamification/lib/gamification";
import { AchievementIcon } from "@/features/gamification/lib/achievementIcons";

// The tracker-owned achievement ids (mirrors the backend tracker block).
const TRACKER_ACHIEVEMENT_IDS = new Set([
  "first_session",
  "ten_sessions",
  "volume_50h",
  "first_debrief",
  "debrief_5",
  "session_leak_found",
]);

export function TrackerAchievements() {
  const { data } = useQuery({
    queryKey: ["gamification"],
    queryFn: getGamification,
    staleTime: 30_000,
  });

  if (!data) return null;
  const badges = data.achievements.filter((a) =>
    TRACKER_ACHIEVEMENT_IDS.has(a.id),
  );
  if (badges.length === 0) return null;

  const earned = badges.filter((b) => b.earned).length;

  return (
    <section className="rounded-2xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-text">Tracker badges</h2>
        <Link to="/skills/level" className="text-xs text-text-muted hover:text-text transition">
          {earned}/{badges.length} · view all
        </Link>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {badges.map((b) => (
          <div
            key={b.id}
            title={`${b.title} — ${b.description}`}
            className={[
              "flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-center",
              b.earned
                ? "border-accent/40 bg-accent/10"
                : "border-border bg-bg opacity-50",
            ].join(" ")}
          >
            <AchievementIcon
              name={b.icon}
              size={18}
              className={b.earned ? "text-accent" : "text-text-muted"}
            />
            <span className="text-[10px] leading-tight text-text-muted line-clamp-2">
              {b.title}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
